import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useGetMessagesScheduleMutation } from 'services/api/adminCampaigns.api';

interface MessageSchedulePreviewProps {
  messagesToSendPerPeriod: number;
  totalClients: number;
  timePeriodInMinutes: number;
  startDate: Date | null;
  startHour: number | null;
  endHour: number | null;
  sendOnWeekend: boolean;
}

/**
 * Component that displays a preview of the message sending schedule
 */
const MessageSchedulePreview: React.FC<MessageSchedulePreviewProps> = ({
  messagesToSendPerPeriod,
  totalClients,
  timePeriodInMinutes,
  startDate,
  startHour,
  endHour,
  sendOnWeekend,
}) => {
  const [getSchedule, { data: schedule, isLoading, error }] = useGetMessagesScheduleMutation();
  const [scheduleEntries, setScheduleEntries] = useState<{ timestamp: number; messages: number }[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);

  /**
   * Fetches the schedule when dependencies change
   */
  useEffect(() => {
    if (totalClients > 0) {
      getSchedule({
        messagesToSendPerPeriod,
        totalClients,
        timePeriodInMinutes,
        campaignStartTime: startDate || undefined,
        startHour: startHour ?? undefined,
        endHour: endHour ?? undefined,
        sendOnWeekend,
      }).catch((e) => {
        console.error(e);
      });
    }
  }, [
    messagesToSendPerPeriod,
    totalClients,
    timePeriodInMinutes,
    startDate,
    startHour,
    endHour,
    sendOnWeekend,
    getSchedule,
  ]);

  /**
   * Processes the schedule response into displayable entries
   */
  useEffect(() => {
    if (schedule) {
      const entries = Object.entries(schedule)
        .map(([timestamp, messages]) => ({
          timestamp: parseInt(timestamp, 10),
          messages,
        }))
        .sort((a, b) => a.timestamp - b.timestamp);
      setScheduleEntries(entries);
      setIsExpanded(false);
    }
  }, [schedule]);

  /**
   * Formats timestamp to readable date/time string
   */
  const formatDateTime = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('es-CL', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (totalClients === 0) {
    return null;
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
        }}
      >
        <CircularProgress />
        <Typography
          sx={{
            ml: 2,
          }}
        >
          Calculando cronograma...
        </Typography>
      </Box>
    );
  }

  if (error) {
    const errorData = error as { data?: string; originalStatus?: number };
    const isSchedulingError =
      errorData.originalStatus === 400 && errorData.data?.includes('Unable to schedule all messages within 15 days');

    if (isSchedulingError) {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            No es posible programar todos los mensajes dentro de los próximos 15 días
          </Typography>
          <Typography variant="body2">
            Con la configuración actual, el envío de mensajes tomaría más de 15 días. Por favor, ajusta los parámetros:
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 0 }}>
            <li>
              <Typography variant="body2">Aumenta el número de mensajes por período</Typography>
            </li>
            <li>
              <Typography variant="body2">Reduce el tiempo entre períodos</Typography>
            </li>
            <li>
              <Typography variant="body2">Amplía el horario de operación (startHour / endHour)</Typography>
            </li>
            <li>
              <Typography variant="body2">Habilita el envío durante fines de semana</Typography>
            </li>
          </Box>
        </Alert>
      );
    }

    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error al calcular el cronograma. Por favor, verifica los parámetros de configuración.
      </Alert>
    );
  }

  if (!schedule || scheduleEntries.length === 0) {
    return null;
  }

  const totalScheduledMessages = scheduleEntries.reduce((sum, entry) => sum + entry.messages, 0);
  const firstSend = scheduleEntries[0];
  const lastSend = scheduleEntries[scheduleEntries.length - 1];
  const previewEntries = scheduleEntries.slice(0, 10);
  const hasMore = scheduleEntries.length > 10;
  const displayedEntries = isExpanded ? scheduleEntries : previewEntries;

  return (
    <Box
      sx={{
        mt: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 2,
        }}
      >
        <Paper elevation={1} sx={{ p: 2, flex: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            Total de Mensajes
          </Typography>
          <Typography variant="h6">{totalScheduledMessages}</Typography>
        </Paper>
        <Paper elevation={1} sx={{ p: 2, flex: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            Primer Envío
          </Typography>
          <Typography variant="body2">{formatDateTime(firstSend.timestamp)}</Typography>
        </Paper>
        <Paper elevation={1} sx={{ p: 2, flex: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            Último Envío
          </Typography>
          <Typography variant="body2">{formatDateTime(lastSend.timestamp)}</Typography>
        </Paper>
        <Paper elevation={1} sx={{ p: 2, flex: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            Total de Períodos
          </Typography>
          <Typography variant="h6">{scheduleEntries.length}</Typography>
        </Paper>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6">Vista Previa del Cronograma</Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            Muestra cuándo se enviarán los mensajes según la configuración actual
          </Typography>
        </Box>
        {hasMore && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => setIsExpanded(!isExpanded)}
            startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          >
            {isExpanded ? 'Mostrar menos' : `Mostrar todos (${scheduleEntries.length})`}
          </Button>
        )}
      </Box>
      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Fecha y Hora</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Mensajes a Enviar</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedEntries.map((entry) => (
              <TableRow key={entry.timestamp} hover>
                <TableCell>{formatDateTime(entry.timestamp)}</TableCell>
                <TableCell align="right">{entry.messages}</TableCell>
              </TableRow>
            ))}
            {hasMore && !isExpanded && (
              <TableRow>
                <TableCell colSpan={2} align="center" sx={{ py: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    ... y {scheduleEntries.length - 10} períodos más
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default MessageSchedulePreview;
